import React, { useEffect } from 'react';
import { useCart } from '../component/CartContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Cart = (props) => {
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const navigate = useNavigate();

  // Safe price calculation — guard against NaN
  const totalPrice = cartItems.reduce((total, item) => {
    const price = parseFloat(item.price) || 0;
    const qty = parseInt(item.quantity) || 0;
    return total + price * qty;
  }, 0);

  // Redirect to login if not signed in
  useEffect(() => {
    if (!props.isSigned) {
      const timer = setTimeout(() => navigate('/Login'), 3000);
      return () => clearTimeout(timer);
    }
  }, [navigate, props.isSigned]);

  // Redirect to /product if cart is empty
  useEffect(() => {
    if (props.isSigned && cartItems.length === 0) {
      navigate('/product');
    }
  }, [cartItems, props.isSigned, navigate]);

  // Not signed in — redirect screen
  if (!props.isSigned) {
    return (
      <motion.div
        className="min-h-screen flex justify-center items-center"
        style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #0f1f0f 100%)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="text-center px-6">
          <motion.div
            className="mb-6 text-6xl"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            🔒
          </motion.div>
          <motion.h1
            className="text-3xl font-bold mb-3"
            style={{ color: '#4ade80', fontFamily: "'Syne', sans-serif" }}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Sign in required
          </motion.h1>
          <motion.p
            className="text-gray-400 text-base"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            Redirecting you to the login page…
          </motion.p>
          <motion.div
            className="mt-6 h-1 rounded-full mx-auto"
            style={{ background: '#4ade80', width: '200px' }}
            initial={{ scaleX: 0, originX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 3, ease: 'linear' }}
          />
        </div>
      </motion.div>
    );
  }

  // Empty cart — useEffect handles redirect, show brief loading state
  if (!cartItems.length) {
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #0f1f0f 100%)' }}>
        <p className="text-gray-400 text-lg">Redirecting to shop…</p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-10 px-4"
      style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #0f1f0f 100%)' }}
    >
      {/* Google Font */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=DM+Sans:wght@400;500;600&display=swap');
        * { box-sizing: border-box; }
        .cart-item-row { transition: background 0.2s; }
        .cart-item-row:hover { background: rgba(74,222,128,0.07); }
        .qty-input::-webkit-inner-spin-button,
        .qty-input::-webkit-outer-spin-button { opacity: 1; }
      `}</style>

      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <motion.div
          className="mb-8 flex items-center justify-between"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div>
            <h2 style={{ fontFamily: "'Syne', sans-serif", color: '#f0fdf4', fontSize: '2rem', fontWeight: 800, lineHeight: 1 }}>
              Your Cart
            </h2>
            <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px', fontFamily: "'DM Sans', sans-serif" }}>
              {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link
            to="/product"
            style={{ color: '#4ade80', fontFamily: "'DM Sans', sans-serif", fontSize: '0.875rem', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            ← Continue shopping
          </Link>
        </motion.div>

        {/* Cart Items */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <AnimatePresence>
            {cartItems.map((item, i) => {
              const price = parseFloat(item.price) || 0;
              const qty = parseInt(item.quantity) || 0;
              const subtotal = price * qty;

              return (
                <motion.div
                  key={item.id}
                  className="cart-item-row"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 40, height: 0 }}
                  transition={{ delay: i * 0.05 }}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(74,222,128,0.12)',
                    borderRadius: '14px',
                    padding: '16px',
                    display: 'flex',
                    gap: '14px',
                    alignItems: 'center',
                  }}
                >
                  {/* Image */}
                  <img
                    src={item.imageUrl}
                    alt={item.name}
                    style={{
                      width: '70px',
                      height: '70px',
                      borderRadius: '10px',
                      objectFit: 'cover',
                      flexShrink: 0,
                      border: '1px solid rgba(74,222,128,0.2)',
                    }}
                  />

                  {/* Info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{
                      fontFamily: "'Syne', sans-serif",
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#f0fdf4',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      marginBottom: '4px',
                    }}>
                      {item.name}
                    </p>
                    <p style={{ fontFamily: "'DM Sans', sans-serif", color: '#4ade80', fontSize: '0.875rem', marginBottom: '10px' }}>
                      ₦{price.toLocaleString()} each
                    </p>

                    {/* Qty + Remove */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        background: 'rgba(0,0,0,0.4)',
                        border: '1px solid rgba(74,222,128,0.2)',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}>
                        <button
                          onClick={() => updateQuantity(item.id, Math.max(1, qty - 1))}
                          style={{ padding: '4px 10px', color: '#4ade80', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', fontFamily: "'DM Sans', sans-serif" }}
                        >−</button>
                        <span style={{ padding: '4px 8px', color: '#f0fdf4', fontFamily: "'DM Sans', sans-serif", fontSize: '0.9rem', minWidth: '28px', textAlign: 'center' }}>
                          {qty}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, qty + 1)}
                          style={{ padding: '4px 10px', color: '#4ade80', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', fontFamily: "'DM Sans', sans-serif" }}
                        >+</button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        style={{ color: '#f87171', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', fontFamily: "'DM Sans', sans-serif" }}
                      >
                        Remove
                      </button>
                    </div>
                  </div>

                  {/* Subtotal */}
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <p style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, color: '#f0fdf4', fontSize: '1rem' }}>
                      ₦{subtotal.toLocaleString()}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          style={{
            marginTop: '24px',
            background: 'rgba(74,222,128,0.07)',
            border: '1px solid rgba(74,222,128,0.2)',
            borderRadius: '16px',
            padding: '20px 24px',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontFamily: "'DM Sans', sans-serif", color: '#9ca3af', fontSize: '0.9rem' }}>
              Subtotal ({cartItems.length} item{cartItems.length !== 1 ? 's' : ''})
            </span>
            <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, color: '#4ade80', fontSize: '1.4rem' }}>
              ₦{totalPrice.toLocaleString()}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Link
              to="/checkout"
              style={{
                flex: 1,
                minWidth: '160px',
                textAlign: 'center',
                background: '#16a34a',
                color: '#fff',
                padding: '13px 20px',
                borderRadius: '10px',
                fontFamily: "'Syne', sans-serif",
                fontWeight: 700,
                fontSize: '0.95rem',
                textDecoration: 'none',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = '#15803d'}
              onMouseLeave={e => e.currentTarget.style.background = '#16a34a'}
            >
              Proceed to Checkout →
            </Link>

            <button
              onClick={clearCart}
              style={{
                padding: '13px 20px',
                background: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.3)',
                color: '#f87171',
                borderRadius: '10px',
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 500,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'background 0.2s',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.18)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(248,113,113,0.1)'}
            >
              Clear Cart
            </button>
          </div>
        </motion.div>

      </div>
    </div>
  );
};

export default Cart;